import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MainCurrencyDto } from '../_core/_model/currency';
import { CreateDrafrOrder, OrderDto, OrderSnapShot } from '../_core/_model/order';
import { ApiService } from '../_core/_services/api.service';
import { AuthService } from '../_core/_services/auth.service';
import { FormatService } from '../_core/_services/format';
import { NotificationService } from '../_core/_services/notification.service';
import { OrderDraftService } from '../_core/_services/order-draft-service';
import { OrderEntryPairChange } from '../common/widget/order-entry/order-entry';

@Component({
  selector: 'app-home',
  standalone: false,
  templateUrl: './home.html',
  styleUrl: './home.css',
})
export class Home implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  loading = false;
  error = '';
  loggedIn = false;
  currencies: MainCurrencyDto[] = [];
  orderSnapshot: OrderSnapShot[] = [];
  recent: OrderDto[] = [];
  buy: OrderDto[] = [];
  sell: OrderDto[] = [];
  displayName = '';
  initials = '';
  fee = 0.0;
  currentTime = new Date();
  selectedOrderPair: OrderEntryPairChange | null = null;

  constructor(
    private notifyService: NotificationService,
    private api: ApiService,
    private auth: AuthService,
    private format: FormatService,
    private router: Router,
    private draft: OrderDraftService
  ) {}

  ngOnInit(): void {
    this.auth.getCurrentUser().pipe(takeUntil(this.destroy$)).subscribe();

    this.auth.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.loggedIn = !!user;

        if (!user) {
          this.displayName = '';
          this.initials = '';
          return;
        }

        this.displayName = `${user.firstName} ${user.lastName}`;
        this.initials = this.getInitials(user.firstName, user.lastName);
      });

    this.draft.pop();
    this.loadMain();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadMain(): void {
    this.loading = true;

    this.api.getMain().subscribe({
      next: response => {
        this.currencies = (response.data?.currencies ?? [])
          .filter(currency => currency.status === 'Active');

        const fromCurrency = this.currencies[0]?.currencyCode ?? '';
        const toCurrency = this.currencies.find(currency => currency.currencyCode !== fromCurrency)?.currencyCode ?? '';
        if (fromCurrency && toCurrency) {
          this.selectedOrderPair = { fromCurrency, toCurrency };
          this.LoadMainStat(fromCurrency, toCurrency);
        }
      },
      error: error => {
        this.error = error?.error?.Error?.Details ?? 'Failed to load main data';
      },
      complete: () => (this.loading = false),
    });
  }

  onOrderPairChanged(pair: OrderEntryPairChange): void {
    this.selectedOrderPair = pair;
    this.LoadMainStat(pair.fromCurrency, pair.toCurrency);
  }

  LoadMainStat(from: string, to: string): void {
    this.loading = true;

    this.api.getMainStats(from, to).subscribe({
      next: response => {
        this.orderSnapshot = response.data.orderSnapshot;
        this.recent = response.data.recent ?? [];
        this.buy = response.data.buy ?? [];
        this.sell = response.data.sell ?? [];
      },
      error: error => {
        this.error = error?.error?.Error?.Details ?? 'Failed to load main data';
        this.notifyService.showError(this.error, 'Error');
      },
      complete: () => (this.loading = false),
    });
  }

  getInitials(first: string, last: string): string {
    return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
  }

  logout(): void {
    this.auth.logout().subscribe();
  }

  getOrderPlusMinusStatus(value: number): string {
    return this.format.getPlusMinus(value);
  }

  get limitedSnapshot(): OrderSnapShot[] {
    return this.orderSnapshot?.slice(0, 4) ?? [];
  }

  takeOrder(order: OrderDto): void {
    const price = order.rate;
    const need = order.remainingAmount;
    const have = order.remainingAmount * price;
    const newPrice = need / have;

    const payload: CreateDrafrOrder = {
      haveCurrency: order.toCurrency.code,
      haveAmount: this.format.toNumber(this.format.formatPrice(have)),
      needCurrency: order.fromCurrency.code,
      needAmount: this.format.toNumber(this.format.formatPrice(need)),
      price: this.format.toNumber(this.format.formatPrice(newPrice)),
    };

    this.setDraftData(payload);
  }

  setDraftData(payload: CreateDrafrOrder): void {
    this.draft.set({ ...payload, executionMode: 'flexible' });
    this.router.navigate(['/orders']);
  }
}
